<template>

  <div class="item-new-cont"
    :class="{'hidden-item' : food.hidden === true || hidden_from_order_dashboard == true, 'hidden-can-edit' : ((food.hidden === true || hidden_from_order_dashboard == true) && can_edit === true) }">

    <div v-if="is_loading == true" class="loader">

      <div class="lds-ellipsis">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>

    </div>

    <div v-else>

      <div class="item"
        :style="[{ 'min-height' : '50px', 'background-color': background_color, 'border-bottom' : getBorderBottom() }, custom_styles.item]"
        role="article"
        :aria-label="`Menu item: ${name}. ${description ? description + '.' : ''} ${food.price ? 'Price: ' + food.price + ' ' + currency + '.' : ''}${allergenText}`"
        tabindex="0"
        @keydown="handleKeyNavigation"
        :ref="`item-${food.id}`">

        <div v-lazy-container="{ selector: 'img' }" class="item-content-container"
          :class="{ 'item-content-container-flex-column' : food.category_header === true || food.major_category_header === true, 'item-content-container-centre-aligned' : food.centre_content === true }"
          id="content-box">

          <!-- An image is being moved or swapped onto this item -->
          <div class="item-image-container image-loading" v-if="image_loading === true"
            :class="{ 'full-width-container' : (food.category_header === true || food.major_category_header === true) }"
            :style="[imageContainerStyle, { 'height' : imageStyle.height || '100px' }]">
            <i class="pi pi-spin pi-spinner"></i>
          </div>

          <div class="item-image-container" v-else-if="food.image.url != null && image_hidden === false"
            :class="{ 'full-width-container' : (food.category_header === true || food.major_category_header === true), 'full-width-image-container' : this.full_size_image === true }"
            :style="imageContainerStyle"
            ref="imageContainer"
            @click="toggleFullSizeImage()">

            <div class="image-resize-frame" :class="{ 'image-resize-frame-header' : isHeader }" :style="imageFrameStyle" ref="imageFrame">

              <img
                :class="{ 'food-image' : food.category_header === false && food.major_category_header === false, 'food-image-full-size' : full_size_image === true }"
                :style="imageStyle" :data-src="food.image.url"
                :alt="`Image of ${name}`"
                v-if="food.image.url != null && image_hidden === false"
                ref="image"
                @load="observeImageCrop"
                @dragstart="onImageDragStart"
                @dragend="$emit('image-drag-end')" />

              <!-- Resize handles; no left handle since images sit on the left -->
              <template v-if="can_edit === true && full_size_image === false">
                <div v-for="edge in ['top', 'right', 'bottom']" :key="edge"
                  class="image-resize-grip" :class="[`image-resize-grip-${edge}`, { 'image-resize-grip-active' : image_resize_edge === edge }]"
                  :title="edge === 'right' ? 'Drag to resize width' : 'Drag to resize height'"
                  @pointerdown.stop.prevent="startImageResize(edge, $event)"
                  @click.stop></div>
                <!-- Arrows move the picture within its frame; only shown along the side it's cropped on -->
                <div v-for="direction in imagePanDirections" :key="direction"
                  class="image-pan-arrow" :class="`image-pan-arrow-${direction}`" :title="`Show more of the image's ${direction === 'up' ? 'top' : direction === 'down' ? 'bottom' : direction} side`"
                  @pointerdown.stop @click.stop="panImage(direction)">
                  <i :class="`pi pi-chevron-${direction}`"></i>
                </div>
                <div v-if="customImageWidth !== null || customImageHeight !== null || imagePosition !== null"
                  class="image-resize-reset" title="Reset image size and position"
                  @pointerdown.stop @click.stop="resetImageSize()">
                  <i class="pi pi-replay"></i>
                </div>
              </template>

            </div>

          </div>

          <!-- custom styles: price as a column before the name ("left") -->
          <div v-if="hasPrice() && customPricePosition === 'left'" class="custom-price-left" :style="customPriceStyle">
            <ItemPrice
              :can_edit="can_edit"
              :food="food"
              :font_family="customPriceStyle.fontFamily"
              :color="customPriceStyle.color"
                :item_price="food.price" 
                :second_price="food.second_price_number" 
                :third_price="food.third_price_number"
                :unit="food.unit" 
                :second_unit="food.second_unit" 
                :third_unit="food.third_unit"
              :price_position="'left'"
              :currency="currency"
              :font_size="customPriceStyle.fontSize" />
          </div>

          <div class="item-text-container"
            :class="{ 'item-text-container-beside-price' : hasPrice() && customPricePosition === 'left', 'item-text-container-overlay' : (food.category_header === true || food.major_category_header === true) && food.image.url != null && image_hidden === false, 'item-with-image-name' : food.image.url != null && image_hidden === false, 'centre-align' : (food.centre_content === true && !(menu.paginate === true && food.major_category_header === true)) }">

            <SpecialTag v-if="food.special_tag_for_item != null && food.special_tag_for_item != ''"
              :special_tag_for_item="food.special_tag_for_item" :background_color="item_name_color"
              :text_color="background_color" :font="food.item_name_font" />

            <div class="item-name"
              :class="[{'align-flex-start' : menu.paginate === true, 'item-name-centered' : food.centre_content === true}, food.major_category_header === true ? 'major-category-item-name' : '']">

              <div class="back-arrow" v-if="(menu.paginate === true) && (food.major_category_header === true)"
                :class="{ 'hidden' : current_pagination_page === 1 }" @click.stop="changePage('prev')"
                role="button"
                aria-label="Previous page">
                <DynamicallyColouredArrow :currentColour="item_name_color" />
              </div>

              <h4
                :style="[{ 'font-size' : item_name_font_size, 'color' : item_name_color, 'font-family' : item_name_font }, custom_styles.name]"
                :class="{ 'centered' : menu.paginate === true && food.major_category_header === true }"
                :dir="isRtl(language) ? 'rtl' : null"
                role="heading"
                :aria-label="`Item name: ${name}`">

                {{ name }}

              </h4>

              <div class="next-arrow" v-if="(menu.paginate === true) && (food.major_category_header === true)"
                :class="{ 'hidden' : current_pagination_page === categories.length }" @click.stop="changePage('next')"
                role="button"
                aria-label="Next page">
                <DynamicallyColouredArrow :currentColour="item_name_color" />
              </div>

              

            </div>

            <p class="hidden-tag"
              v-if="(food.hidden === true || hidden_from_order_dashboard === true) & can_edit === true">This item is
              hidden</p>

            <div class="description"
              :style="{ 'color' : item_description_color, 'font-family' : item_description_font }"
              v-if="description != '' && description != null">

              <h5 :style="[{ 'font-size': item_description_font_size, 'width': menu.paginate ? '100%' : null, 'line-height': (food.major_category_header || food.category_header) ? '125%' : '180%' }, custom_styles.description]"
                :class="{ 'centered-description': food.centre_content === true, 'paginate-centered': menu.paginate && food.major_category_header === true }"
                :dir="isRtl(language) ? 'rtl' : null"
                :aria-label="`Description: ${description}`">{{
                description }}</h5>

              <div class="options-container">

              </div>

            </div>
            <FoodTags v-if="!food.category_header && !food.major_category_header && hasFoodTags" :food="food" :foodTagNameFontFamily="item_description_font"  />

            <div v-if="this.$store.getters.getUserType === 'admin'" class="updated-info">
              <img :data-src="require(`../assets/new-update-icon.svg`)" />
              {{ food.last_edited_by }} <span>{{ moment(food.updated_at).fromNow() }}</span>
            </div>

            <!-- Price box - always visible when price exists (unless custom styles put it in a column on the left) -->
            <div v-if="hasPrice() && customPricePosition !== 'left'" 
                 class="order-button-container" 
                 :class="{ 'centered': customPricePosition ? customPricePosition === 'middle' : food.centre_content === true, 'custom-price-right': customPricePosition === 'right', 'custom-price-middle': customPricePosition === 'middle' }"
                 :style="customPricePosition ? customPriceStyle : { 'background-color' : food.background_colour }"
                 role="region"
                 :aria-label="`Price: ${food.price} ${currency}`">
              
              <ItemPrice 
                :can_edit="can_edit" 
                :food="food" 
                :font_family="customPricePosition ? customPriceStyle.fontFamily : item_name_font" 
                :color="customPricePosition ? customPriceStyle.color : food.item_name_font_colour"
                :item_price="food.price" 
                :second_price="food.second_price_number" 
                :third_price="food.third_price_number"
                :unit="food.unit" 
                :second_unit="food.second_unit" 
                :third_unit="food.third_unit"
                :price_position="customPricePosition === 'middle' ? 'middle' : 'default'" 
                :currency="currency" 
                :font_size="customPricePosition ? customPriceStyle.fontSize : item_description_font_size" />

            </div>

            <div v-if="orderable()">

              <OrderButton v-if="food.orderable === true && can_edit === false && item_type === 'item'" :food="food"
                :name="name" :currency="currency" :menu="menu" :foods="foods" :languages="languages"
                :language="language" :selection_groups="selection_groups" :selections="selections"
                :item_name_font="item_name_font" :sold_out_multiple_price_items="sold_out_multiple_price_items" :can_edit="can_edit" />

            </div>

            <div v-if="soldOut() === true" class="sold-out-container">
              <SoldOut :colour="food.item_name_font_colour" />
            </div>

            

          </div>

        </div>

      </div>

    </div>

  </div>


</template>

<script>
import VueDragResize from 'vue-drag-resize'
import ItemPrice from '@/components/ItemPrice.vue'
import ItemOptions from '@/components/ItemOptions.vue'
import FontSelector from '@/components/FontSelector.vue'
import ImageUploader from 'vue-image-upload-resize'
import SpecialTag from '@/components/SpecialTag.vue'
import FoodTags from '@/components/FoodTags.vue'
import OrderButton from '@/components/OrderButton.vue'
import SoldOut from '@/components/miscellaneous/SoldOut.vue'
import DynamicallyColouredArrow from '@/components/dynamic_svgs/DynamicallyColouredArrow.vue'
import { foodCustomStyles } from '@/components/ai/menuCss'
import { isRtl } from '@/languages.js'


export default {
    name: 'Item',
    props: ['food', 'selections', 'session_based_ordering', 'active_order_session', 'sold_out_multiple_price_items', 'selection_groups', 'menu', 'foods', 'language', 'languages', 'can_edit', 'active_component', 'currency', 'selected_items', 'food_tags', 'sold_out_foods', 'hidden_from_order_dashboard', 'random_image_key', 'current_pagination_page', 'categories', 'image_loading'],
    components: {
        VueDragResize,
        ItemPrice,
        ItemOptions,
        FontSelector,
        ImageUploader,
        SpecialTag,
        FoodTags,
        OrderButton,
        SoldOut,
        DynamicallyColouredArrow
    },
    data () {
        return {
            food_id: this.food.id,
            menu_id: this.menu.id,
            edit_item_selected: false,
            name: '',
            description: '',
            item_type: '',
            hasImage: false,
            image: {},
            item_name_font_size: '',
            item_name_color: '',
            item_description_color: '',
            item_description_font_size: '',
            prevent_active: Boolean,
            background_color: '',
            color_picker_toggled: false,
            price_position: String,
            show_item_name_options: false,
            show_item_description_options: false,
            show_background_color_options: false,
            item_name_font: String,
            item_description_font: String,
            price_font: String,
            show_item_name_font_selector: false,
            show_item_description_font_selector: false,
            show_item_price_font_selector: false,
            border_bottom: this.food.stroke_bottom,
            image_selector: false,
            image_height: '',
            image_hidden: false,
            full_size_image: false,
            // Live size while an image resize handle is being dragged (width in %, height in px)
            image_resize_edge: null,
            image_resize_start: null,
            image_resize_width: null,
            image_resize_height: null,
            // Whether object-fit: cover crops the image left/right (x) or top/bottom (y), and a pending pan save
            image_crop_x: false,
            image_crop_y: false,
            image_crop_observer: null,
            image_pan_timer: null,
            image_pan_previous: null,
            is_loading: false,
            container_height: 100,
            url: process.env.VUE_APP_API_URL,
            componentKey: 0
        }
    },
    created() {
        this.preventActive();
        this.getNameAndDescription();
        this.getItemType();
        this.getBackgroundColor();
        this.getFontSize();
        this.getItemNameColor();
        this.getPricePosition();
        this.getFonts();
        this.getBorderBottom();
        this.getImageHeight();
        this.getImageVisibility();
        this.getItemDescriptionColor();
        
    },
    mounted() {
        // Listen for navigation events to focus this item
        this.$root.$on('focus-item', (itemId) => {
            if (itemId === this.food.id) {
                this.focusItem();
            }
        });
    },
    beforeDestroy() {
        // Clean up event listener
        this.$root.$off('focus-item');
        this.stopImageResizeListeners();
        if (this.image_crop_observer) {
            this.image_crop_observer.disconnect()
        }
        if (this.image_pan_timer !== null) {
            clearTimeout(this.image_pan_timer)
            this.saveImagePosition()
        }
    },
    computed: {
        isHeader() {
            return this.food.category_header === true || this.food.major_category_header === true
        },
        // photo_width is a % of the item's width (so it scales with the screen); photo_height is in px.
        customImageWidth() {
            if (this.image_resize_width !== null) {
                return this.image_resize_width
            }
            return this.food.photo_width != null && this.food.photo_width !== 'default' ? Number(this.food.photo_width) : null
        },
        customImageHeight() {
            if (this.image_resize_height !== null) {
                return this.image_resize_height
            }
            return this.food.photo_height != null && this.food.photo_height !== 'default' ? Number(this.food.photo_height) : null
        },
        // Items size the container (it's the flex column beside the text); headers size the frame inside their full-width container.
        imageContainerStyle() {
            if (this.full_size_image === true || this.isHeader || this.customImageWidth === null) {
                return {}
            }
            return { width: `${this.customImageWidth}%` }
        },
        imageFrameStyle() {
            if (this.full_size_image === true || !this.isHeader || this.customImageWidth === null) {
                return {}
            }
            return { width: `${this.customImageWidth}%`, maxWidth: 'none' }
        },
        // photo_object_fit is a CSS object-position ("30% 50%"); 'default' (or null) leaves the image centred.
        imagePosition() {
            const match = /^(\d+(?:\.\d+)?)% (\d+(?:\.\d+)?)%$/.exec(this.food.photo_object_fit || '')
            return match ? { x: Number(match[1]), y: Number(match[2]) } : null
        },
        // Each arrow shows more of the picture on that side, until that edge of the picture is in view
        imagePanDirections() {
            const { x, y } = this.imagePosition || { x: 50, y: 50 }
            const directions = []
            if (this.image_crop_x) {
                if (x > 0) directions.push('left')
                if (x < 100) directions.push('right')
            }
            if (this.image_crop_y) {
                if (y > 0) directions.push('up')
                if (y < 100) directions.push('down')
            }
            return directions
        },
        imageStyle() {
            const style = { height: this.customImageHeight !== null ? `${this.customImageHeight}px` : this.image_height }
            if (this.imagePosition !== null) {
                style.objectPosition = `${this.imagePosition.x}% ${this.imagePosition.y}%`
            }
            if (this.full_size_image === false && this.isHeader) {
                if (this.customImageWidth !== null) {
                    style.maxWidth = 'none'
                }
                if (this.customImageHeight !== null) {
                    style.maxHeight = 'none'
                }
            }
            return style
        },
        // Detected styles (foods.item_css etc.), layered over the regular styles while custom_css_styles is on.
        custom_styles() {
            return foodCustomStyles(this.food)
        },
        // "left" | "middle" | "right" while custom styles are on and a position is set; null otherwise
        // (the price then uses the regular layout and style settings).
        customPricePosition() {
            const position = this.food.custom_price_position
            return this.food.custom_css_styles === true && ['left', 'middle', 'right'].includes(position) ? position : null
        },
        // While custom styles position the price, it matches the custom item name (one path for styles).
        customPriceStyle() {
            const name = this.custom_styles.name
            return {
                fontFamily: name.fontFamily || this.item_name_font,
                color: name.color || this.food.item_name_font_colour,
                fontSize: name.fontSize || this.item_description_font_size,
                fontWeight: name.fontWeight || null
            }
        },
        hasFoodTags() {
            const food = this.food;
            const tagKeys = [
                'spicy', 'spicy_level_two', 'spicy_level_three', 'chef_recommended', 'pork', 'vegetarian', 'halal', 'dairy', 'fish', 'alcohol', 'alcohol_free', 'gluten', 'gluten_free', 'peanuts', 'shellfish', 'egg', 'twenty_four_hours', 'soy_beans', 'walnuts', 'celery', 'mustard', 'sesame_seeds', 'sulfites', 'lupin', 'clams', 'sustainable_fishing', 'dairy_free', 'vegan'
            ];
            return tagKeys.some(key => food[key] === true);
        },
        allergenText() {
            // Generate text description of allergens and dietary info for screen readers
            const food = this.food;
            const tags = [];
            
            const tagMap = {
                'spicy': 'Spicy',
                'spicy_level_two': 'Very spicy',
                'spicy_level_three': 'Extremely spicy',
                'chef_recommended': 'Chef Recommended',
                'pork': 'Contains pork',
                'vegetarian': 'Vegetarian',
                'halal': 'Halal',
                'dairy': 'Contains dairy',
                'fish': 'Contains fish',
                'alcohol': 'Contains Alcohol',
                'alcohol_free': 'Non-Alcoholic',
                'gluten': 'Contains gluten',
                'gluten_free': 'Gluten-Free',
                'peanuts': 'Contains peanuts',
                'shellfish': 'Contains shellfish',
                'egg': 'Contains egg',
                'twenty_four_hours': 'Available 24 Hours',
                'soy_beans': 'Contains soy',
                'walnuts': 'Contains tree nuts',
                'celery': 'Contains celery',
                'mustard': 'Contains mustard',
                'sesame_seeds': 'Contains sesame',
                'sulfites': 'Contains sulfites',
                'lupin': 'Contains lupin',
                'clams': 'Contains clams',
                'sustainable_fishing': 'Sustainable fishing',
                'dairy_free': 'Dairy-Free',
                'vegan': 'Vegan'
            };
            
            Object.keys(tagMap).forEach(key => {
                if (food[key] === true) {
                    tags.push(tagMap[key]);
                }
            });
            
            return tags.length > 0 ? ' ' + tags.join(', ') + '.' : '';
        }
    },
    methods: {
        handleKeyNavigation(event) {
            // Handle arrow key navigation for accessibility
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                
                const currentIndex = this.foods.findIndex(f => f.id === this.food.id);
                let nextIndex;
                
                if (event.key === 'ArrowDown') {
                    nextIndex = currentIndex + 1;
                    if (nextIndex >= this.foods.length) {
                        nextIndex = 0; // Wrap to first item
                    }
                } else if (event.key === 'ArrowUp') {
                    nextIndex = currentIndex - 1;
                    if (nextIndex < 0) {
                        nextIndex = this.foods.length - 1; // Wrap to last item
                    }
                }
                
                // Emit event to focus the next/previous item
                const nextItemId = this.foods[nextIndex].id;
                this.$root.$emit('focus-item', nextItemId);
            }
        },
        focusItem() {
            // Focus this item element
            const itemRef = this.$refs[`item-${this.food.id}`];
            if (itemRef) {
                this.$nextTick(() => {
                    itemRef.focus();
                    // Scroll into view if needed
                    itemRef.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                });
            }
        },
        changePage(direction) {
            if (direction === 'next') {
                this.$emit('paginate-menu-next-page')
            }
            if (direction === 'prev') {
                this.$emit('paginate-menu-prev-page')
            }
        },
        setError (error, text) {
            this.error = (error.response && error.response.data && error.response.data.error) || text
        },
        setImage: function (file) {
            this.hasImage = true
            this.image = file
            const data = new FormData();
            this.is_loading = true
            Object.entries(file).forEach(([key, value]) => { data.append(key, value) })

            this.$http.upload.patch('/app_upload_food_image/' + this.food_id, data
                ).then(response => {
                    this.food.image.url = response.data.url
                    this.image_selector = false
                    this.is_loading = false
                    this.getImageHeight();
                })
                .catch(error => this.setError(error, 'Cannot update food.'))
        },
        onImageDragStart(event) {
            if (this.can_edit !== true) {
                return
            }
            event.dataTransfer.effectAllowed = 'move'
            event.dataTransfer.setData('text/plain', String(this.food.id))
            this.$emit('image-drag-start', this.food.id)
        },
        startImageResize(edge, event) {
            if (this.can_edit !== true) {
                return
            }
            const sized = this.isHeader ? this.$refs.imageFrame : this.$refs.imageContainer
            this.image_resize_start = {
                edge,
                x: event.clientX,
                y: event.clientY,
                width: sized.getBoundingClientRect().width,
                height: this.$refs.image.getBoundingClientRect().height,
                reference_width: this.$refs.imageContainer.parentElement.getBoundingClientRect().width
            }
            this.image_resize_edge = edge
            window.addEventListener('pointermove', this.onImageResizeMove)
            window.addEventListener('pointerup', this.endImageResize)
            window.addEventListener('pointercancel', this.endImageResize)
        },
        onImageResizeMove(event) {
            const start = this.image_resize_start
            if (start.edge === 'right') {
                const percent = (start.width + event.clientX - start.x) / start.reference_width * 100
                this.image_resize_width = Math.round(Math.min(Math.max(percent, 10), this.isHeader ? 100 : 70) * 10) / 10
            } else {
                const delta = event.clientY - start.y
                const height = start.edge === 'bottom' ? start.height + delta : start.height - delta
                this.image_resize_height = Math.round(Math.min(Math.max(height, 30), 600))
            }
        },
        endImageResize() {
            this.stopImageResizeListeners()
            this.image_resize_edge = null
            // The pointer may be released over the item, whose click would select it or open the full-size image
            const swallowClick = (e) => e.stopPropagation()
            window.addEventListener('click', swallowClick, true)
            setTimeout(() => window.removeEventListener('click', swallowClick, true), 0)

            const changes = {}
            if (this.image_resize_width !== null) {
                changes.photo_width = String(this.image_resize_width)
            }
            if (this.image_resize_height !== null) {
                changes.photo_height = String(this.image_resize_height)
            }
            this.image_resize_width = null
            this.image_resize_height = null
            if (Object.keys(changes).length > 0) {
                this.saveImageSize(changes)
            }
        },
        resetImageSize() {
            if (this.image_pan_timer !== null) {
                clearTimeout(this.image_pan_timer)
                this.image_pan_timer = null
            }
            this.saveImageSize({ photo_width: 'default', photo_height: 'default', photo_object_fit: 'default' })
        },
        saveImageSize(changes, previous = null) {
            previous = previous || Object.fromEntries(Object.keys(changes).map(key => [key, this.food[key]]))
            // $set, since photo_object_fit is missing from foods loaded before the column existed
            Object.keys(changes).forEach(key => this.$set(this.food, key, changes[key]))
            this.$http.secured.post('/app_update_food/' + this.menu_id + '/' + this.food.id, { food: changes })
                .catch(error => {
                    Object.keys(previous).forEach(key => this.$set(this.food, key, previous[key]))
                    this.setError(error, 'Cannot update image.')
                    alert(this.error)
                })
        },
        // Re-check which way the image is cropped whenever it loads or its frame changes size
        observeImageCrop() {
            if (this.image_crop_observer) {
                this.image_crop_observer.disconnect()
            }
            if (typeof ResizeObserver !== 'undefined') {
                this.image_crop_observer = new ResizeObserver(() => this.measureImageCrop())
                this.image_crop_observer.observe(this.$refs.image)
            }
            this.measureImageCrop()
        },
        measureImageCrop() {
            const image = this.$refs.image
            if (!image || !image.naturalWidth || !image.clientHeight) {
                return
            }
            const naturalRatio = image.naturalWidth / image.naturalHeight
            const frameRatio = image.clientWidth / image.clientHeight
            this.image_crop_x = naturalRatio > frameRatio * 1.02
            this.image_crop_y = naturalRatio < frameRatio / 1.02
        },
        // Clicks move the picture 10% at a time; a burst of clicks is saved once, so requests can't land out of order
        panImage(direction) {
            const position = this.imagePosition || { x: 50, y: 50 }
            const [dx, dy] = { left: [-10, 0], right: [10, 0], up: [0, -10], down: [0, 10] }[direction]
            const x = Math.min(Math.max(position.x + dx, 0), 100)
            const y = Math.min(Math.max(position.y + dy, 0), 100)
            if (this.image_pan_timer === null) {
                this.image_pan_previous = this.food.photo_object_fit
            } else {
                clearTimeout(this.image_pan_timer)
            }
            this.$set(this.food, 'photo_object_fit', `${x}% ${y}%`)
            this.image_pan_timer = setTimeout(() => this.saveImagePosition(), 500)
        },
        saveImagePosition() {
            this.image_pan_timer = null
            this.saveImageSize({ photo_object_fit: this.food.photo_object_fit }, { photo_object_fit: this.image_pan_previous })
        },
        stopImageResizeListeners() {
            window.removeEventListener('pointermove', this.onImageResizeMove)
            window.removeEventListener('pointerup', this.endImageResize)
            window.removeEventListener('pointercancel', this.endImageResize)
        },
        toggleFullSizeImage() {
            if (this.item_type === 'item') {
                this.full_size_image = !this.full_size_image
            }
        },
        toggleImageSelector(food_id) {
            if (this.image_selector === false) {
                this.image_selector = true 
            } else {
                this.image_selector = false
            }
        },
        orderable() {
            if (this.menu.session_based_ordering === true) {
                if (this.active_order_session === true) {
                    return true
                }
            } else {
                if ((this.menu.orderable === true || this.menu.orders_for_delivery === true || this.menu.orders_for_pickup === true || this.menu.orders_for_in_room == true) && !this.sold_out_foods.includes(this.food.id)) {
                    return true
                }
            }   
        },
        soldOut() {
          if (this.sold_out_foods != null && this.sold_out_foods.includes(this.food.id)) {
            return true
          }
        },
        toggleFontSelector(font_selector_type) {
            this.show_item_name_options = false
            if (this.can_edit) {
                if (font_selector_type === 'item_name') {
                    if (this.show_item_name_font_selector === true) {
                        this.show_item_name_font_selector = false
                    } else {
                        this.show_item_name_font_selector = true
                        this.show_item_description_font_selector = false
                        this.show_item_price_font_selector = false
                    }
                }

                if (font_selector_type === 'item_description') {
                    if (this.show_item_description_font_selector === true) {
                        this.show_item_description_font_selector = false
                    } else {
                        this.show_item_description_font_selector = true
                        this.show_item_name_font_selector = false
                        this.show_item_price_font_selector = false
                    }
                }
                
                if (font_selector_type === 'price') {
                    if (this.show_item_price_font_selector === true) {
                        this.show_item_price_font_selector = false
                    } else {
                        this.show_item_price_font_selector = true
                        this.show_item_description_font_selector = false
                        this.show_item_name_font_selector = false                        
                    }
                }            
            }
        },
        selectItemNameFont: function (font) {
            for (let i = 0; i < this.selected_items.length; i++) {
                this.$http.secured.post('/app_update_food/' + this.menu_id + '/' + this.selected_items[i], { food: 
                    { 
                        item_name_font: font
                    }}).then(response => {
                        this.item_name_font = font
                        this.$root.$emit('selected-items-change-item-name-font', font);
                    })
                    .catch(error => this.setError(error, 'Cannot update food.'))
            }
        },
        addPageBreak() {
            for (let i = 0; i < this.selected_items.length; i++) {
                this.$http.secured.post('/app_update_food/' + this.menu_id + '/' + this.selected_items[i], { food: 
                    { 
                        page_break: true
                    }}).then(response => {
                        this.food.page_break = true
                    })
                    .catch(error => this.setError(error, 'Cannot update food.'))
            }
        },
        removePageBreak() {
            for (let i = 0; i < this.selected_items.length; i++) {
                this.$http.secured.post('/app_update_food/' + this.menu_id + '/' + this.selected_items[i], { food: 
                    { 
                        page_break: false
                    }}).then(response => {
                        this.food.page_break = false
                    })
                    .catch(error => this.setError(error, 'Cannot update food.'))
            }
        },
        selectItemDescriptionFont: function (font) {
            for (let i = 0; i < this.selected_items.length; i++) {
                this.$http.secured.post('/app_update_food/' + this.menu_id + '/' + this.selected_items[i], { food: 
                    { 
                        item_description_font: font
                    }}).then(response => {
                        this.item_description_font = font
                        this.$root.$emit('selected-items-change-item-description-font', font);
                        // show_item_description_font_selector = false
                    })
                    .catch(error => this.setError(error, 'Cannot update food.'))
            }
        },
        selectPriceFont: function (font) {
            for (let i = 0; i < this.selected_items.length; i++) {
                this.$http.secured.post('/app_update_food/' + this.menu_id + '/' + this.selected_items[i], { food: 
                    { 
                        price_font: font
                    }}).then(response => {
                        this.price_font = font
                        this.$root.$emit('selected-items-change-price-font', font);
                        // show_item_price_font_selector = false
                    })
                    .catch(error => this.setError(error, 'Cannot update food.'))
            }
        },                
      getBackgroundColour() {
        if (this.food.background_colour === 'default' || this.food.background_colour === null || this.food.background_colour === undefined || this.food.background_colour == '') {
              this.background_colour = '#FFFFFF'
            } else {
              this.background_colour = this.food.background_colour
            }
        },
        getItemType() {
            if (this.food.major_category_header === true) {
                this.item_type = 'major-category-header'
            } else if (this.food.category_header === true) {
                this.item_type = 'minor-category-header'
                // console.log('minor cat header found')
            } else {
                this.item_type = 'item'
            }
        },
        getImageVisibility() {
            if (this.food.image_hidden === true) {
                this.image_hidden = true
            } else {
                this.image_hidden = false
            }
        },
        hideImage() {
            if (this.image_hidden === true) {
                this.image_hidden = false
            } else {
                this.image_hidden = true
            }
            this.food.image_hidden = this.image_hidden
            this.$http.secured.post('/app_update_food/' + this.menu_id + '/' + this.food.id, { food: 
                { 
                    image_hidden: this.image_hidden
                }}).then(response => {

                })
                .catch(error => this.setError(error, 'Cannot update food.'))
        },
        preventActive() {
            if (this.can_edit === true) {
                this.prevent_active = false
            } else {
                this.prevent_active = true
            }
        },
        getFonts() {
            if (this.food.item_name_font === 'default') {
                this.item_name_font = 'Montserrat'
            } else {
                this.item_name_font = this.food.item_name_font
            }

            if (this.food.item_description_font === 'default') {
                this.item_description_font = 'Montserrat'
            } else {
                this.item_description_font = this.food.item_description_font
            }
            
            if (this.food.price_font === 'default') {
                this.price_font = 'Montserrat'
            } else {
                this.price_font = this.food.price_font
            }
        },
        getImageHeight() {
            if (this.food.category_header === true || this.food.major_category_header === true) {
                    this.image_height = String((Number(this.food.container_height)) + 'px')
            } else {
                if (this.food.container_height === 'default') {
                    this.image_height = '100px'
                } else {
                    if (this.food.container_height < 100) {
                        this.image_height = String((Number(this.food.container_height)) + 'px')
                    } else {
                        this.image_height = String('100px')
                    }
                }  
            }         
        },
        isRtl,
        hasPrice() {
            return this.food.price != null && this.food.price != 0
        },
        getBorderBottom() {
            if (this.border_bottom === true) {
                return '1px solid lightgray'
            } else {
                return '0px'
            }
        },
        toggleBorderBottom() {
            if (this.border_bottom === true) {
                this.border_bottom = false
            } else {
                this.border_bottom = true
            }
            for (let i = 0; i < this.selected_items.length; i++) {
                this.$http.secured.post('/app_update_food/' + this.menu_id + '/' + this.selected_items[i], { food: 
                    { 
                        stroke_bottom: this.border_bottom
                    }}).then(response => {
                        this.$root.$emit('selected-items-change-border-bottom', this.border_bottom);
                    })
                    .catch(error => this.setError(error, 'Cannot update food.'))
            }            
        },
        getPricePosition() {
            if (this.food.price_position == "default") {
                this.price_position = "default"
            } else {
                this.price_position = "align_left"
            }
        },
        setPricePosition: function (position) {
            // console.log('price position ' + position)
            for (let i = 0; i < this.selected_items.length; i++) {
                this.$http.secured.post('/app_update_food/' + this.menu_id + '/' + this.selected_items[i], { food: 
                    { 
                        price_position: position
                    }}).then(response => {
                        this.price_position = position
                        this.$root.$emit('selected-items-change-price-position', position);
                    })
                    .catch(error => this.setError(error, 'Cannot update food.'))
            }
        },
        getHeight(food) {
            var height = 0
            if (food.container_height === "default") {
                height = 100
            } else {
                height = food.container_height
            }
            if (this.can_edit === false) {
                if (this.menu.orderable === true || this.menu.orders_for_pickup === true || this.menu.orders_for_delivery === true || this.menu.orders_for_in_room === true) {
                    if (food.orderable === true) {
                        height = parseInt(height) + parseInt(22)
                        return parseInt(height)
                    } else {
                        return Number(height)
                    }
                } else {
                    return Number(height)
                }
            } else {
                return Number(height)
            }
        },
        getNameAndDescription() {
            this.name = this.food['translation_' + this.language] || this.food.name || this.food.translation_en
            this.description = this.food['description_' + this.language] || this.food.description || this.food.description_en     
        },
        resize(food)  {
            this.$http.secured.post('/app_update_food/' + this.menu_id + '/' + this.food_id, { food: 
                { 
                    container_height: food.height 
                }}).then(response => {
                    // console.log(response.data)
                    this.food.container_height = food.height
                    this.getImageHeight();
                })
                .catch(error => this.setError(error, 'Cannot update food.'))
        },
        toggleItemNameOptions() {
            if (this.show_item_name_options === false) {
                this.show_item_name_options = true
                this.show_item_description_font_selector = false
                this.show_item_name_font_selector = false
                this.show_item_price_font_selector = false
            } else {
                this.show_item_name_options = false
            }
        },
        toggleBackgroundColorOptions() {
            // console.log('background options clicked')
            if (this.show_background_color_options === false) {
                this.show_background_color_options = true
            } else {
                this.show_background_color_options = false
            }
        },
        increaseItemNameFontSize() {
            var startingFontSize = this.item_name_font_size.replace(/\D/g,'');
            var newFontSize = (Number(startingFontSize) + 2)
            this.item_name_font_size = String(newFontSize + 'px')
            // console.log(newFontSize)
            for (let i = 0; i < this.selected_items.length; i++) {
                this.$http.secured.post('/app_update_food/' + this.menu_id + '/' + this.selected_items[i], { food: 
                    { 
                        item_name_font_size: this.item_name_font_size
                    }}).then(response => {
                        // console.log(response.data)
                        this.$root.$emit('selected-items-change-font-size', this.item_name_font_size);
                    })
                    .catch(error => this.setError(error, 'Cannot update food.'))
            }
        },
        decreaseItemNameFontSize() {
            var startingFontSize = this.item_name_font_size.replace(/\D/g,'');
            var newFontSize = (Number(startingFontSize) - 2)
            this.item_name_font_size = String(newFontSize + 'px')
            for (let i = 0; i < this.selected_items.length; i++) {
                this.$http.secured.post('/app_update_food/' + this.menu_id + '/' + this.selected_items[i], { food: 
                    { 
                        item_name_font_size: this.item_name_font_size
                    }}).then(response => {
                        this.$root.$emit('selected-items-change-font-size', this.item_name_font_size);
                    })
                    .catch(error => this.setError(error, 'Cannot update food.'))
            }
        }, 
        getItemNameColor() {
            this.item_name_color = this.food.item_name_font_colour
        },
        getItemDescriptionColor() {
            if (this.food.item_description_font_colour === 'default') {
                this.item_description_color = '#000000'
            } else {
                this.item_description_color = this.food.item_description_font_colour
            }
        },
        getBackgroundColor() {
            if (this.food.background_colour === 'default' || this.food.background_colour === null || this.food.background_colour === undefined || this.food.background_colour == '') {
                this.background_color = '#FFFFFF'
            } else {
                this.background_color = this.food.background_colour
            }
        },
        setItemBackgroundColor: function (color) {
            for (let i = 0; i < this.selected_items.length; i++) {
                this.$http.secured.post('/app_update_food/' + this.menu_id + '/' + this.selected_items[i], { food: 
                    { 
                        background_colour: color
                    }}).then(response => {
                        this.background_color = color
                        this.$root.$emit('selected-items-change-background-color', color);
                        if (localStorage.colour != undefined) {
                            var storedColours = JSON.parse(localStorage.colour)
                        } else {
                            var storedColours = []
                        }             
                        storedColours.indexOf(color) === -1 ? storedColours.push(color) : console.log("This item already exists");
                        localStorage.setItem("colour", JSON.stringify(storedColours));
                    })
                    .catch(error => this.setError(error, 'Cannot update food.'))
            }
        },
        setColour() {
            // console.log('color')
        },
        getFontSize() {
            if (this.food.item_name_font_size === 'default') {
                if (this.item_type === 'major-category-header') {
                    this.item_name_font_size = '25px'
                } else if (this.item_type === 'minor-category-header') {
                    this.item_name_font_size = '20px'
                } else {
                    this.item_name_font_size = '15px'
                }
            } else {
                this.item_name_font_size = this.food.item_name_font_size
            }
            
            if (this.food.item_description_font_size === 'default') {
                this.item_description_font_size = '12px'
            } else {
                this.item_description_font_size = this.food.item_description_font_size
            }

        }
    }
}


</script>

<style scoped>

.updated-info {
    background-color: rgba(39, 169, 225, 0.7);
    color: white;
    font-size: 9px;
    box-sizing: border-box;
    padding: 2.5px 5px 2.5px 5px;
    position: absolute;
    top: 2.5px;
    right: 2.5px;
    display: flex;
    flex-direction: row;
    align-items: center;
    font-weight: 600;
    border-radius: 2.5px;
}

.updated-info span {
    margin-left: 2.5px;
    color: #F8ED31;
}

.updated-info img {
    width: 10px;
    height: 10px;
    margin-right: 5px;
}

h4 {
    font-weight: 600;
    width: 100%;
    display: flex;
    flex-direction: row;
    width: max-content;
    margin-right: 5px;
    margin-bottom: 0px;
    margin: 0px 0px 0px 0px;
}

.centered {
    width: 100%;
    justify-content: center !important;
    /* justify-content: space-evenly; */
}

.item-content-container {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    width: 100% !important;
}

.item-content-container-flex-column {
    flex-direction: column;
}

.item {
    position: relative;
    width: 100% !important;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    padding: 15px 20px 18px 20px;
    cursor: pointer;
    height: min-content !important;
    margin-bottom: 0px;
}

.item-unpadded {
    padding: 0px;
}

.item:hover {
    background-color: rgba(177, 214, 241, 0.1);
    transition: 0.1s;
}

.item:focus {
    outline: 2px solid #4794CB;
    outline-offset: -2px;
    background-color: rgba(177, 214, 241, 0.15);
    transition: 0.1s;
}

.item:focus-visible {
    outline: 2px solid #4794CB;
    outline-offset: -2px;
    background-color: rgba(177, 214, 241, 0.15);
}

.food-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.food-image-full-size {
    height: auto !important;
    max-height: 1000px;
}

.full-width-image-container {
    background-color: rgba(255, 255, 255, 0.8);
    width: 100% !important;
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 10000000;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    

}

.full-width-image-container img {
    width: 90%;
    max-width: 600px;
    margin: auto;
    height: auto !important;
}

.description {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin: 6px 0px 0px 0px;

}

.description h5 {
    margin: 0px 0px 0px 0px;
    font-weight: 400;
    font-size: 12px;
    line-height: 120%;
}

.item-image-container {
    /* margin-right: 10px; */
    /* width: 15%; */
    width: 30%;
    margin-right: 2.5%;
    /* border: 1px dotted lightgray; */
}

.full-width {
    width: 100%;
}

/* IMAGE RESIZING */
.image-resize-frame {
    position: relative;
    width: 100%;
}

.image-resize-frame-header {
    max-width: 500px;
}

/* Full-size view centres the image, as it did before the frame wrapped it */
.full-width-image-container .image-resize-frame {
    width: 100%;
    height: 100%;
    max-width: none;
    display: flex;
    align-items: center;
}

.image-resize-grip {
    position: absolute;
    z-index: 10;
    touch-action: none;
    opacity: 0;
    transition: opacity 0.15s;
}

.image-resize-grip::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: white;
    border: 1px solid #28A8E0;
    border-radius: 3px;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
}

.image-resize-grip-top,
.image-resize-grip-bottom {
    left: 0;
    right: 0;
    height: 12px;
    cursor: ns-resize;
}

.image-resize-grip-top {
    top: -6px;
}

.image-resize-grip-bottom {
    bottom: -6px;
}

.image-resize-grip-top::after,
.image-resize-grip-bottom::after {
    width: 24px;
    height: 6px;
}

.image-resize-grip-right {
    top: 0;
    bottom: 0;
    right: -6px;
    width: 12px;
    cursor: ew-resize;
}

.image-resize-grip-right::after {
    width: 6px;
    height: 24px;
}

.image-resize-reset {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 11;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: white;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.15s;
}

.image-resize-reset .pi {
    font-size: 10px;
    color: #708090;
}

.image-pan-arrow {
    position: absolute;
    z-index: 11;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.85);
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.15s;
}

.image-pan-arrow .pi {
    font-size: 10px;
    color: #708090;
}

/* Kept clear of the resize grips on the top, bottom and right edges */
.image-pan-arrow-left,
.image-pan-arrow-right {
    top: 50%;
    transform: translateY(-50%);
}

.image-pan-arrow-left {
    left: 4px;
}

.image-pan-arrow-right {
    right: 10px;
}

.image-pan-arrow-up,
.image-pan-arrow-down {
    left: 50%;
    transform: translateX(-50%);
}

.image-pan-arrow-up {
    top: 10px;
}

.image-pan-arrow-down {
    bottom: 10px;
}

.image-resize-frame:hover .image-resize-grip,
.image-resize-frame:hover .image-resize-reset,
.image-resize-frame:hover .image-pan-arrow,
.image-resize-grip-active {
    opacity: 1;
}

@media (hover: none) {
    .image-resize-grip,
    .image-resize-reset,
    .image-pan-arrow {
        opacity: 1;
    }
}

.image-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.05);
}

.image-loading .pi {
    font-size: 20px;
    color: #708090;
}

.full-width-container {
    width: 100% !important;
    height: max-content;
}

.full-width-container img {
    width: 100%;
    max-width: 500px;
    max-height: 200px;
    object-fit: cover;
    position: relative;

}

.item-text-container {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.item-text-container-overlay {
    position: absolute;
    left: 30px;
}

.item-text-container-overlay h4 {
    /* color: white; */
    text-shadow: 0 0 16px rgba(0, 0, 0, 1);
}

.item-text-container-overlay h5 {
    /* color: white; */
    text-shadow: 0 0 16px rgba(0, 0, 0, 1);    
}

.item-name {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: flex-start;
    justify-content: space-between;
    position: relative;
    margin: 0px 0px 0px 0px;
}

.item-name-centered {
    max-width: 100%;
    text-align: center;
    margin: auto;
    margin-right: 0px;
    margin-left: 0px;
    justify-content: center;
}

.align-flex-start {
    align-items: center;
}

.item-with-image-name {
    max-width: 78.5%;
}

.hidden {
    opacity: 0;
}

.item-name h4 {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    flex-wrap: wrap;
    box-sizing: border-box;
    padding-right: 0px;
    /* width: 70%; */
    /* max-width: 90%; */
}

#drag {
    width: 20px;
    height: 20px;
    background-color: gray;
}

.vdr.active {
    z-index: 1 !important;
}

.item-new-cont {
    width: 100% !important;
    min-height: max-content !important;
}

.options-container {
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-right: 10px;
}

.more-options {
    position: relative;
    /* position: absolute; */
    height: 20px !important;
    width: 20px !important;
    margin-left: 10px;
    background-color: white;
    display: flex;
    justify-content: center;
    align-content: center;
    box-sizing: border-box;
    padding: 5px;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    color: white;
    font-weight: 600;
    cursor: pointer;
    /* z-index: 1000; */
}

.more-options:hover {
    box-shadow: 0 0 16px rgba(0, 0, 0, 0.2);
    transition: 0.2s;
    cursor: pointer;
}

.more-options-selected {
    background-color: #4794CB;
}

.has-selections {
    background-color: #4794CB;
}

.more-options-selected img {
    filter: brightness(0) invert(1);
}


.color-picker-container {
    position: absolute;
    right: 5px;
    z-index: 100;
    height: 100%;
    flex-grow: 1;
    display: flex;
    justify-content: flex-end;
    flex-direction: column;
    align-self: flex-end;

}

.color-picker {
    box-sizing: border-box;
    padding: 2.5px;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);
    /* border: 1px solid lightgray; */
    background-color: white;
    width: 20px;
    height: 20px;
    max-width: 20px;
    max-height: 20px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    position: relative;
    /* z-index: 8000; */
}

.color-picker img {
    max-height: 15px !important;
    max-width: 15px !important;;
    min-height: 15px !important;;
    min-width: 15px !important;;
}
.border-picker {
    margin-top: 10px;
}

.border-picker-selected {
    background-color: #4794CB;
}

.border-picker-selected img {
    filter: brightness(0) invert(1);
}

.image-uploader {
    height: 20px;
    display: flex;
    align-items: center;
    /* margin-left: 10px; */
}

.more-options:hover > .button-descriptor {
    display: flex;
    z-index: 100000;
    position: absolute;
}

.color-picker:hover > .button-descriptor {
    display: flex;
    z-index: 100000;
    position: absolute;
}

.hidden-tag {
    /* position: absolute; */
    background-color: black;
    color: white;
    box-sizing: border-box;
    padding: 3px;
    width: max-content;
    font-size: 12px;
    border-radius: 3px;
}

.hidden-item {
    display: none;
}

.hidden-can-edit {
    opacity: 0.3;
    display: block;
    width: 100% !important;
}

.next-arrow {
    width: 25px;
    height: 25px;
}

.back-arrow {
    width: 25px;
    height: 25px;
    transform: rotate(180deg);
}

.centre-align {
    flex-direction: column;
    align-items: center;
}

.centered-description {
    text-align: center
}

.item-content-container-centre-aligned {
        flex-direction: column;
    align-items: center;
}

.sold-out-container {
  width: 100%;
  display: flex;
  justify-content: flex-end;
}

.paginate-centered {
  text-align: center;
}

/* custom styles: the price as a column before the name (custom_price_position "left") */
.custom-price-left {
    flex-shrink: 0;
    min-width: 60px;
    padding-right: 20px;
    box-sizing: border-box;
}

/* beside the price column the text takes the remaining width and wraps, instead of width: 100% pushing it off-screen */
.item-text-container-beside-price {
    width: auto;
    flex: 1 1 0;
    min-width: 0;
}

.custom-price-left ::v-deep .price-box {
    flex-direction: column;
    align-items: flex-start;
}

.custom-price-left ::v-deep .number-container {
    justify-content: flex-start;
}

.order-button-container {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: flex-start;
    justify-content: flex-start;
    box-sizing: border-box;
    margin: 9px 0px 0px 0px;
}

.order-button-container.centered {
    justify-content: center;
}

/* custom styles: the price right-aligned / centred under the description (stacked prices follow on small screens) */
.order-button-container.custom-price-right {
    justify-content: flex-end;
}

.custom-price-right ::v-deep .price-box {
    align-items: flex-end;
}

.custom-price-middle ::v-deep .price-box {
    align-items: center;
}

@media(max-width: 500px) {
    .custom-price-left {
        min-width: 0;
        padding-right: 12px;
    }
}


@media(max-width: 500px) {
    /* .food-image {
        max-width: 125px;
        min-width: 125px;
    } */
    .item-image-container {
        width: 30%;
        /* min-width: 100px; */
    }

}

@media(max-width: 400px) {
    /* .food-image {
        max-width: 100px;
        min-width: 100px;
    } */

    

    .content-container {
        /* height: max-content !important; */
        padding: 5px 0px 10px 0px;
    }
}

</style>